import { createTextNode as _$createTextNode } from "@opentui/solid";
import { effect as _$effect } from "@opentui/solid";
import { insertNode as _$insertNode } from "@opentui/solid";
import { insert as _$insert } from "@opentui/solid";
import { createComponent as _$createComponent } from "@opentui/solid";
import { use as _$use } from "@opentui/solid";
import { setProp as _$setProp } from "@opentui/solid";
import { spread as _$spread } from "@opentui/solid";
import { mergeProps as _$mergeProps } from "@opentui/solid";
import { createElement as _$createElement } from "@opentui/solid";
import { pathToFileURL } from "bun";
import fuzzysort from "fuzzysort";
import { firstBy } from "remeda";
import { createMemo, createResource, createEffect, onMount, onCleanup, Index, Show, createSignal } from "solid-js";
import { createStore } from "solid-js/store";
import { useSDK } from "@tui/context/sdk";
import { useSync } from "@tui/context/sync";
import { useTheme, selectedForeground } from "@tui/context/theme";
import { SplitBorder } from "@tui/component/border";
import { useCommandDialog } from "@tui/component/dialog-command";
import { useTerminalDimensions } from "@opentui/solid";
import { Locale } from "@/util/locale";
import { useFrecency } from "./frecency";
function removeLineRange(input) {
  const hashIndex = input.lastIndexOf("#");
  return hashIndex !== -1 ? input.substring(0, hashIndex) : input;
}
function extractLineRange(input) {
  const hashIndex = input.lastIndexOf("#");
  if (hashIndex === -1) {
    return {
      baseQuery: input
    };
  }
  const baseName = input.substring(0, hashIndex);
  const linePart = input.substring(hashIndex + 1);
  const lineMatch = linePart.match(/^(\d+)(?:-(\d*))?$/);
  if (!lineMatch) {
    return {
      baseQuery: baseName
    };
  }
  const startLine = Number(lineMatch[1]);
  const endLine = lineMatch[2] && startLine < Number(lineMatch[2]) ? Number(lineMatch[2]) : undefined;
  return {
    lineRange: {
      baseName,
      startLine,
      endLine
    },
    baseQuery: baseName
  };
}
export function Autocomplete(props) {
  const sdk = useSDK();
  const sync = useSync();
  const command = useCommandDialog();
  const {
    theme
  } = useTheme();
  const dimensions = useTerminalDimensions();
  const frecency = useFrecency();
  const [store, setStore] = createStore({
    index: 0,
    selected: 0,
    visible: false,
    input: "keyboard"
  });
  const [positionTick, setPositionTick] = createSignal(0);
  createEffect(() => {
    if (store.visible) {
      let lastPos = {
        x: 0,
        y: 0,
        width: 0
      };
      const interval = setInterval(() => {
        const anchor = props.anchor();
        if (anchor.x !== lastPos.x || anchor.y !== lastPos.y || anchor.width !== lastPos.width) {
          lastPos = {
            x: anchor.x,
            y: anchor.y,
            width: anchor.width
          };
          setPositionTick(t => t + 1);
        }
      }, 50);
      onCleanup(() => clearInterval(interval));
    }
  });
  const position = createMemo(() => {
    if (!store.visible) return {
      x: 0,
      y: 0,
      width: 0
    };
    const dims = dimensions();
    positionTick();
    const anchor = props.anchor();
    const parent = anchor.parent;
    const parentX = parent?.x ?? 0;
    const parentY = parent?.y ?? 0;
    return {
      x: anchor.x - parentX,
      y: anchor.y - parentY,
      width: anchor.width
    };
  });
  const filter = createMemo(() => {
    if (!store.visible) return;
    // Track props.value to make memo reactive to text changes
    props.value; // <- there surely is a better way to do this, like making .input() reactive

    return props.input().getTextRange(store.index + 1, props.input().cursorOffset);
  });

  // filter() reads reactive props.value plus non-reactive cursor/text state.
  // On keypress those can be briefly out of sync, so filter() may return an empty/partial string.
  // Copy it into search in an effect because effects run after reactive updates have been rendered and painted
  // so the input has settled and all consumers read the same stable value.
  const [search, setSearch] = createSignal("");
  createEffect(() => {
    const next = filter();
    setSearch(next ? next : "");
  });

  // When the filter changes due to how TUI works, the mousemove might still be triggered
  // via a synthetic event as the layout moves underneath the cursor. This is a workaround to make sure the input mode remains keyboard so
  // that the mouseover event doesn't trigger when filtering.
  createEffect(() => {
    filter();
    setStore("input", "keyboard");
  });
  function insertPart(text, part) {
    const input = props.input();
    const currentCursorOffset = input.cursorOffset;
    const charAfterCursor = props.value.at(currentCursorOffset);
    const needsSpace = charAfterCursor !== " ";
    const append = "@" + text + (needsSpace ? " " : "");
    input.cursorOffset = store.index;
    const startCursor = input.logicalCursor;
    input.cursorOffset = currentCursorOffset;
    const endCursor = input.logicalCursor;
    input.deleteRange(startCursor.row, startCursor.col, endCursor.row, endCursor.col);
    input.insertText(append);
    const virtualText = "@" + text;
    const extmarkStart = store.index;
    const extmarkEnd = extmarkStart + Bun.stringWidth(virtualText);
    const styleId = part.type === "file" ? props.fileStyleId : part.type === "agent" ? props.agentStyleId : undefined;
    const extmarkId = input.extmarks.create({
      start: extmarkStart,
      end: extmarkEnd,
      virtual: true,
      styleId,
      typeId: props.promptPartTypeId()
    });
    props.setPrompt(draft => {
      if (part.type === "file") {
        const existingIndex = draft.parts.findIndex(p => p.type === "file" && "url" in p && p.url === part.url);
        if (existingIndex !== -1) {
          const existing = draft.parts[existingIndex];
          if (part.source?.text && existing && "source" in existing && existing.source && "text" in existing.source && existing.source.text) {
            existing.source.text.start = extmarkStart;
            existing.source.text.end = extmarkEnd;
            existing.source.text.value = virtualText;
          }
          return;
        }
      }
      if (part.type === "file" && part.source?.text) {
        part.source.text.start = extmarkStart;
        part.source.text.end = extmarkEnd;
        part.source.text.value = virtualText;
      } else if (part.type === "agent" && part.source) {
        part.source.start = extmarkStart;
        part.source.end = extmarkEnd;
        part.source.value = virtualText;
      }
      const partIndex = draft.parts.length;
      draft.parts.push(part);
      props.setExtmark(partIndex, extmarkId);
    });
    if (part.type === "file" && part.source && part.source.type === "file") {
      frecency.updateFrecency(part.source.path);
    }
  }
  const [files] = createResource(() => search(), async query => {
    if (!store.visible || store.visible === "/") return [];
    const {
      lineRange,
      baseQuery
    } = extractLineRange(query ?? "");

    // Get files from SDK
    const result = await sdk.client.find.files({
      query: baseQuery
    });
    const options = [];

    // Add file options
    if (!result.error && result.data) {
      const sortedFiles = result.data.sort((a, b) => {
        const aScore = frecency.getFrecency(a);
        const bScore = frecency.getFrecency(b);
        if (aScore !== bScore) return bScore - aScore;
        const aDepth = a.split("/").length;
        const bDepth = b.split("/").length;
        if (aDepth !== bDepth) return aDepth - bDepth;
        return a.localeCompare(b);
      });
      const width = props.anchor().width - 4;
      options.push(...sortedFiles.map(item => {
        const baseDir = (sync.data.path.directory || process.cwd()).replace(/\/+$/, "");
        const fullPath = `${baseDir}/${item}`;
        const urlObj = pathToFileURL(fullPath);
        let filename = item;
        if (lineRange && !item.endsWith("/")) {
          filename = `${item}#${lineRange.startLine}${lineRange.endLine ? `-${lineRange.endLine}` : ""}`;
          urlObj.searchParams.set("start", String(lineRange.startLine));
          if (lineRange.endLine !== undefined) {
            urlObj.searchParams.set("end", String(lineRange.endLine));
          }
        }
        const url = urlObj.href;
        const isDir = item.endsWith("/");
        return {
          display: Locale.truncateMiddle(filename, width),
          value: filename,
          isDirectory: isDir,
          path: item,
          onSelect: () => {
            insertPart(filename, {
              type: "file",
              mime: "text/plain",
              filename,
              url,
              source: {
                type: "file",
                text: {
                  start: 0,
                  end: 0,
                  value: ""
                },
                path: item
              }
            });
          }
        };
      }));
    }
    return options;
  }, {
    initialValue: []
  });
  const mcpResources = createMemo(() => {
    if (!store.visible || store.visible === "/") return [];
    const options = [];
    const width = props.anchor().width - 4;
    for (const res of Object.values(sync.data.mcp_resource)) {
      const text = `${res.name} (${res.uri})`;
      options.push({
        display: Locale.truncateMiddle(text, width),
        value: text,
        description: res.description,
        onSelect: () => {
          insertPart(res.name, {
            type: "file",
            mime: res.mimeType ?? "text/plain",
            filename: res.name,
            url: res.uri,
            source: {
              type: "resource",
              text: {
                start: 0,
                end: 0,
                value: ""
              },
              clientName: res.client,
              uri: res.uri
            }
          });
        }
      });
    }
    return options;
  });
  const agents = createMemo(() => {
    const agents = sync.data.agent;
    return agents.filter(agent => !agent.hidden && agent.mode !== "primary").map(agent => ({
      display: "@" + agent.name,
      onSelect: () => {
        insertPart(agent.name, {
          type: "agent",
          name: agent.name,
          source: {
            start: 0,
            end: 0,
            value: ""
          }
        });
      }
    }));
  });
  const commands = createMemo(() => {
    const results = [...command.slashes()];
    for (const serverCommand of sync.data.command) {
      if (serverCommand.source === "skill") continue;
      const label = serverCommand.source === "mcp" ? ":mcp" : "";
      results.push({
        display: "/" + serverCommand.name + label,
        description: serverCommand.description,
        onSelect: () => {
          const newText = "/" + serverCommand.name + " ";
          const cursor = props.input().logicalCursor;
          props.input().deleteRange(0, 0, cursor.row, cursor.col);
          props.input().insertText(newText);
          props.input().cursorOffset = Bun.stringWidth(newText);
        }
      });
    }
    results.sort((a, b) => a.display.localeCompare(b.display));
    const max = firstBy(results, [x => x.display.length, "desc"])?.display.length;
    if (!max) return results;
    return results.map(item => ({
      ...item,
      display: item.display.padEnd(max + 2)
    }));
  });
  const options = createMemo(prev => {
    const filesValue = files();
    const agentsValue = agents();
    const commandsValue = commands();
    const mixed = store.visible === "@" ? [...agentsValue, ...(filesValue || []), ...mcpResources()] : [...commandsValue];
    const searchValue = search();
    if (!searchValue) {
      return mixed;
    }
    if (files.loading && prev && prev.length > 0) {
      return prev;
    }
    const result = fuzzysort.go(removeLineRange(searchValue), mixed, {
      keys: [obj => removeLineRange((obj.value ?? obj.display).trimEnd()), "description", obj => obj.aliases?.join(" ") ?? ""],
      limit: 10,
      scoreFn: objResults => {
        const displayResult = objResults[0];
        let score = objResults.score;
        if (displayResult && displayResult.target.startsWith(store.visible + searchValue)) {
          score *= 2;
        }
        const frecencyScore = objResults.obj.path ? frecency.getFrecency(objResults.obj.path) : 0;
        return score * (1 + frecencyScore);
      }
    });
    return result.map(arr => arr.obj);
  });
  createEffect(() => {
    filter();
    setStore("selected", 0);
  });
  function move(direction) {
    if (!store.visible) return;
    if (!options().length) return;
    let next = store.selected + direction;
    if (next < 0) next = options().length - 1;
    if (next >= options().length) next = 0;
    moveTo(next);
  }
  function moveTo(next) {
    setStore("selected", next);
    if (!scroll) return;
    const viewportHeight = Math.min(height(), options().length);
    const scrollBottom = scroll.scrollTop + viewportHeight;
    if (next < scroll.scrollTop) {
      scroll.scrollBy(next - scroll.scrollTop);
    } else if (next + 1 > scrollBottom) {
      scroll.scrollBy(next + 1 - scrollBottom);
    }
  }
  function select() {
    const selected = options()[store.selected];
    if (!selected) return;
    hide();
    selected.onSelect?.();
  }
  function expandDirectory() {
    const selected = options()[store.selected];
    if (!selected) return;
    const input = props.input();
    const currentCursorOffset = input.cursorOffset;
    const displayText = selected.display.trimEnd();
    const path = displayText.startsWith("@") ? displayText.slice(1) : displayText;
    input.cursorOffset = store.index;
    const startCursor = input.logicalCursor;
    input.cursorOffset = currentCursorOffset;
    const endCursor = input.logicalCursor;
    input.deleteRange(startCursor.row, startCursor.col, endCursor.row, endCursor.col);
    input.insertText("@" + path);
    setStore("selected", 0);
  }
  function show(mode) {
    command.keybinds(false);
    setStore({
      visible: mode,
      index: props.input().cursorOffset
    });
  }
  function hide() {
    const text = props.input().plainText;
    if (store.visible === "/" && !text.endsWith(" ") && text.startsWith("/")) {
      const cursor = props.input().logicalCursor;
      props.input().deleteRange(0, 0, cursor.row, cursor.col);
      // Sync the prompt store immediately since onContentChange is async
      props.setPrompt(draft => {
        draft.input = props.input().plainText;
      });
    }
    command.keybinds(true);
    setStore("visible", false);
  }
  onMount(() => {
    props.ref({
      get visible() {
        return store.visible;
      },
      onInput(value) {
        if (store.visible) {
          if (
          // Typed text before the trigger
          props.input().cursorOffset <= store.index ||
          // There is a space between the trigger and the cursor
          props.input().getTextRange(store.index, props.input().cursorOffset).match(/\s/) ||
          // "/<command>" is not the sole content
          store.visible === "/" && value.match(/^\S+\s+\S+\s*$/)) {
            hide();
          }
          return;
        }

        // Check if autocomplete should reopen (e.g., after backspace deleted a space)
        const offset = props.input().cursorOffset;
        if (offset === 0) return;

        // Check for "/" at position 0 - reopen slash commands
        if (value.startsWith("/") && !value.slice(0, offset).match(/\s/)) {
          show("/");
          setStore("index", 0);
          return;
        }

        // Check for "@" trigger - find the nearest "@" before cursor with no whitespace between
        const text = value.slice(0, offset);
        const idx = text.lastIndexOf("@");
        if (idx === -1) return;
        const between = text.slice(idx);
        const before = idx === 0 ? undefined : value[idx - 1];
        if ((before === undefined || /\s/.test(before)) && !between.match(/\s/)) {
          show("@");
          setStore("index", idx);
        }
      },
      onKeyDown(e) {
        if (store.visible) {
          const name = e.name?.toLowerCase();
          const ctrlOnly = e.ctrl && !e.meta && !e.shift;
          const isNavUp = name === "up" || ctrlOnly && name === "p";
          const isNavDown = name === "down" || ctrlOnly && name === "n";
          if (isNavUp) {
            setStore("input", "keyboard");
            move(-1);
            e.preventDefault();
            return;
          }
          if (isNavDown) {
            setStore("input", "keyboard");
            move(1);
            e.preventDefault();
            return;
          }
          if (name === "escape") {
            hide();
            e.preventDefault();
            return;
          }
          if (name === "return") {
            select();
            e.preventDefault();
            return;
          }
          if (name === "tab") {
            const selected = options()[store.selected];
            if (selected?.isDirectory) {
              expandDirectory();
            } else {
              select();
            }
            e.preventDefault();
            return;
          }
        }
        if (!store.visible) {
          if (e.name === "@") {
            const cursorOffset = props.input().cursorOffset;
            const charBeforeCursor = cursorOffset === 0 ? undefined : props.input().getTextRange(cursorOffset - 1, cursorOffset);
            const canTrigger = charBeforeCursor === undefined || charBeforeCursor === "" || /\s/.test(charBeforeCursor);
            if (canTrigger) show("@");
          }
          if (e.name === "/") {
            if (props.input().cursorOffset === 0) show("/");
          }
        }
      }
    });
  });
  const height = createMemo(() => {
    const count = options().length || 1;
    if (!store.visible) return Math.min(10, count);
    positionTick();
    return Math.min(10, count, Math.max(1, props.anchor().y));
  });
  let scroll;
  return (() => {
    var _el$ = _$createElement("box"),
      _el$2 = _$createElement("scrollbox");
    _$insertNode(_el$, _el$2);
    _$setProp(_el$, "position", "absolute");
    _$setProp(_el$, "zIndex", 100);
    _$spread(_el$, _$mergeProps({
      get visible() {
        return store.visible !== false;
      },
      get top() {
        return position().y - height();
      },
      get left() {
        return position().x;
      },
      get width() {
        return position().width;
      }
    }, SplitBorder, {
      get borderColor() {
        return theme.border;
      }
    }), true);
    _$use(r => scroll = r, _el$2);
    _$setProp(_el$2, "scrollbarOptions", {
      visible: false
    });
    _$insert(_el$2, _$createComponent(Index, {
      get each() {
        return options();
      },
      get fallback() {
        return (() => {
          var _el$3 = _$createElement("box"),
            _el$4 = _$createElement("text");
          _$insertNode(_el$3, _el$4);
          _$setProp(_el$3, "paddingLeft", 1);
          _$setProp(_el$3, "paddingRight", 1);
          _$insertNode(_el$4, _$createTextNode(`No matching items`));
          _$effect(_$p => _$setProp(_el$4, "fg", theme.textMuted, _$p));
          return _el$3;
        })();
      },
      children: (option, index) => (() => {
        var _el$6 = _$createElement("box"),
          _el$7 = _$createElement("text");
        _$insertNode(_el$6, _el$7);
        _$setProp(_el$6, "paddingLeft", 1);
        _$setProp(_el$6, "paddingRight", 1);
        _$setProp(_el$6, "flexDirection", "row");
        _$setProp(_el$6, "onMouseMove", () => {
          setStore("input", "mouse");
        });
        _$setProp(_el$6, "onMouseOver", () => {
          if (store.input !== "mouse") return;
          moveTo(index);
        });
        _$setProp(_el$6, "onMouseDown", () => {
          setStore("input", "mouse");
          moveTo(index);
        });
        _$setProp(_el$6, "onMouseUp", () => select());
        _$setProp(_el$7, "flexShrink", 0);
        _$insert(_el$7, () => option().display);
        _$insert(_el$6, _$createComponent(Show, {
          get when() {
            return option().description;
          },
          get children() {
            var _el$8 = _$createElement("text");
            _$setProp(_el$8, "wrapMode", "none");
            _$insert(_el$8, () => option().description);
            _$effect(_$p => _$setProp(_el$8, "fg", index === store.selected ? selectedForeground(theme) : theme.textMuted, _$p));
            return _el$8;
          }
        }), null);
        _$effect(_p$ => {
          var _v$3 = index === store.selected ? theme.primary : undefined,
            _v$4 = index === store.selected ? selectedForeground(theme) : theme.text;
          _v$3 !== _p$.e && (_p$.e = _$setProp(_el$6, "backgroundColor", _v$3, _p$.e));
          _v$4 !== _p$.t && (_p$.t = _$setProp(_el$7, "fg", _v$4, _p$.t));
          return _p$;
        }, {
          e: undefined,
          t: undefined
        });
        return _el$6;
      })()
    }));
    _$effect(_p$ => {
      var _v$ = theme.backgroundMenu,
        _v$2 = height();
      _v$ !== _p$.e && (_p$.e = _$setProp(_el$2, "backgroundColor", _v$, _p$.e));
      _v$2 !== _p$.t && (_p$.t = _$setProp(_el$2, "height", _v$2, _p$.t));
      return _p$;
    }, {
      e: undefined,
      t: undefined
    });
    return _el$;
  })();
}